import { useProtecaoFormulario } from "@/shared/hooks/useProtecaoSaida";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useVoltarOu } from "@/modules/visitantes/hooks/useVoltarOu";
import { FormularioVisitante } from "@/modules/visitantes/components/FormularioVisitante";
import { PaginaFormulario, SemPermissao } from "@/modules/visitantes/components/PaginaFormulario";
import { ResumoPendente } from "@/modules/visitantes/components/ResumoPendente";
import {
  useAdicionarInformado,
  useIndicacoes,
  usePapelVisitantes,
  useVisitantes,
} from "@/modules/visitantes/hooks/useVisitantes";
import type { DadosVisitante } from "@/modules/visitantes/services/visitantesService";
import { indicacaoParaDados } from "@/modules/visitantes/utils/conversao";

/** O ministério responsável confere (e ajusta, se quiser) os dados do Visitante pendente e o adiciona (ou não). */
export function AvaliarInformadoPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const voltar = useVoltarOu("/visitantes?aba=informados");
  const protecao = useProtecaoFormulario();
  const { papel, carregando } = usePapelVisitantes();
  const { data: informados, isLoading } = useIndicacoes(papel === "gestao");
  const { data: existentes = [] } = useVisitantes(papel === "gestao");
  const adicionar = useAdicionarInformado();
  const [erro, setErro] = useState<string | null>(null);

  if (carregando || (papel === "gestao" && isLoading))
    return <p className="px-4 py-10 text-center text-sm text-muted">Carregando…</p>;
  if (papel !== "gestao")
    return <SemPermissao texto="Só o ministério responsável avalia os visitantes pendentes." />;
  const informado = informados?.find((i) => i.id === id);
  if (!informado || informado.situacao !== "pendente") {
    return <SemPermissao texto="Informação não encontrada ou já avaliada." />;
  }

  async function enviar(dados: DadosVisitante) {
    if (!informado) return;
    setErro(null);
    try {
      const novoId = await adicionar.mutateAsync({
        id: informado.id,
        versao: informado.versao,
        dados,
      });
      protecao.liberar();
      navigate("/visitantes", {
        replace: true,
        state: {
          aviso: informado.familiares?.length
            ? `${dados.nome} e mais ${informado.familiares.length} da família foram adicionados aos visitantes.`
            : `${dados.nome} foi adicionado(a) aos visitantes.`,
          novoId,
        },
      });
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível adicionar o visitante.");
    }
  }

  return (
    <PaginaFormulario
      protecao={protecao}
      titulo="Adicionar visitante pendente"
      descricao={`Cadastrado por ${informado.de ?? "um líder"}${informado.ministerio ? ` (${informado.ministerio})` : ""}. Confira os dados antes de adicionar.`}
    >
      <ResumoPendente pendente={informado} />
      <FormularioVisitante
        modo="avaliacao"
        inicial={indicacaoParaDados(informado)}
        existentes={existentes}
        enviando={adicionar.isPending}
        erro={erro}
        onEnviar={enviar}
        onCancelar={voltar}
      />
    </PaginaFormulario>
  );
}
